import { apiRequest, dersdinleAuth, type DersdinleUser } from "@/lib/api";

type Filter = { field: string; operator: string; value: unknown };
type Order = { field: string; ascending: boolean };

type ApiResult<T = unknown> = Promise<{ data: T | null; error: { message: string } | null; count?: number | null }>;

class QueryBuilder {
  private readonly table: string;
  private readonly mutation: { operation: "insert" | "update" | "delete" | "upsert"; payload?: unknown; options?: unknown } | undefined;
  private readonly filters: Filter[] = [];
  private readonly orders: Order[] = [];
  private selected = "*";
  private take: number | null = null;
  private one = false;
  private optionalOne = false;
  constructor(table: string, mutation?: QueryBuilder["mutation"]) {
    this.table = table;
    this.mutation = mutation;
  }

  select(columns = "*", options?: { count?: "exact"; head?: boolean }) {
    this.selected = columns;
    if (options?.head) this.take = 0;
    return this;
  }
  eq(field: string, value: unknown) { this.filters.push({ field, operator: "eq", value }); return this; }
  neq(field: string, value: unknown) { this.filters.push({ field, operator: "neq", value }); return this; }
  gte(field: string, value: unknown) { this.filters.push({ field, operator: "gte", value }); return this; }
  gt(field: string, value: unknown) { this.filters.push({ field, operator: "gt", value }); return this; }
  lte(field: string, value: unknown) { this.filters.push({ field, operator: "lte", value }); return this; }
  lt(field: string, value: unknown) { this.filters.push({ field, operator: "lt", value }); return this; }
  in(field: string, value: unknown[]) { this.filters.push({ field, operator: "in", value }); return this; }
  is(field: string, value: unknown) { this.filters.push({ field, operator: "is", value }); return this; }
  contains(field: string, value: unknown[]) { this.filters.push({ field, operator: "contains", value }); return this; }
  order(field: string, options?: { ascending?: boolean }) { this.orders.push({ field, ascending: options?.ascending !== false }); return this; }
  limit(value: number) { this.take = value; return this; }
  single() { this.one = true; return this; }
  maybeSingle() { this.optionalOne = true; return this; }

  then<TResult1 = { data: unknown; error: { message: string } | null }, TResult2 = never>(
    onfulfilled?: ((value: { data: unknown; error: { message: string } | null }) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ) {
    return this.execute().then(onfulfilled, onrejected);
  }

  private async execute(): ApiResult<unknown> {
    try {
      const result = this.mutation
        ? await apiRequest<{ data: unknown; count?: number }>("/data/mutate", {
            method: "POST",
            body: { table: this.table, filters: this.filters, ...this.mutation },
          })
        : await apiRequest<{ data: unknown; count?: number }>("/data/query", {
            method: "POST",
            body: { table: this.table, columns: this.selected, filters: this.filters, orders: this.orders, limit: this.take, single: this.one || this.optionalOne },
          });
      return { data: result.data ?? null, error: null, count: result.count ?? null };
    } catch (error) {
      return { data: null, error: { message: error instanceof Error ? error.message : "Veri işlemi tamamlanamadı." } };
    }
  }
}

let cachedUser: DersdinleUser | null = null;
const subscribers = new Set<(event: string, session: { user: DersdinleUser } | null) => void>();
function notify(event: string, user: DersdinleUser | null) {
  cachedUser = user;
  subscribers.forEach((subscriber) => subscriber(event, user ? { user } : null));
}

export const getDataClient = (): any => ({
  from(table: string) {
    return {
      select: (columns = "*", options?: { count?: "exact"; head?: boolean }) => new QueryBuilder(table).select(columns, options),
      insert: (payload: unknown) => new QueryBuilder(table, { operation: "insert", payload }),
      update: (payload: unknown) => new QueryBuilder(table, { operation: "update", payload }),
      delete: () => new QueryBuilder(table, { operation: "delete" }),
      upsert: (payload: unknown, options?: unknown) => new QueryBuilder(table, { operation: "upsert", payload, options }),
    };
  },
  auth: {
    async getUser() {
      if (cachedUser) return { data: { user: cachedUser }, error: null };
      try { const { user } = await dersdinleAuth.me(); cachedUser = user; return { data: { user }, error: null }; }
      catch { return { data: { user: null }, error: null }; }
    },
    async getSession() {
      const { data } = await this.getUser();
      return { data: { session: data.user ? { user: data.user, access_token: "" } : null }, error: null };
    },
    async signInWithPassword(input: { email: string; password: string; otp?: string }) {
      try { const { user } = await dersdinleAuth.login(input.email, input.password, input.otp); notify("SIGNED_IN", user); return { data: { user, session: { user, access_token: "" } }, error: null }; }
      catch (error) { return { data: { user: null, session: null }, error: { message: error instanceof Error ? error.message : "Giriş yapılamadı." } }; }
    },
    async signUp(input: { email: string; password: string; options?: { data?: Record<string, string> } }) {
      try {
        const meta = input.options?.data ?? {};
        const { user } = await dersdinleAuth.register({ email: input.email, password: input.password, fullName: meta.ad_soyad || input.email.split("@")[0], role: (meta.role as "ogrenci" | "veli" | "ogretmen") || "ogrenci", gradeLevel: meta.sinif, city: meta.sehir, phone: meta.telefon, birthDate: meta.dogum_tarihi });
        notify("SIGNED_IN", user);
        return { data: { user, session: { user, access_token: "" } }, error: null };
      } catch (error) { return { data: { user: null, session: null }, error: { message: error instanceof Error ? error.message : "Kayıt oluşturulamadı." } }; }
    },
    async signOut() { try { await dersdinleAuth.logout(); } finally { notify("SIGNED_OUT", null); } return { error: null }; },
    async resetPasswordForEmail(email: string) {
 try {await apiRequest('/password-recovery.php',{method:'POST',body:{action:'request',email}});return {error:null};}
 catch(error){return {error:{message:error instanceof Error?error.message:'İşlem tamamlanamadı.'}};}
},
    async updateUser() { return { error: { message: "Parola güncelleme yerel güvenlik modülüne taşınıyor." } }; },
    onAuthStateChange(callback: (event: string, session: { user: DersdinleUser } | null) => void) { subscribers.add(callback); return { data: { subscription: { unsubscribe: () => subscribers.delete(callback) } } }; },
    mfa: {
      async listFactors() { return { data: { totp: [] }, error: { message: "İki aşamalı doğrulama yerel güvenlik modülüne taşınıyor." } }; },
      async enroll() { return { data: null, error: { message: "İki aşamalı doğrulama henüz etkin değil." } }; },
      async challenge() { return { data: null, error: { message: "İki aşamalı doğrulama henüz etkin değil." } }; },
      async verify() { return { error: { message: "İki aşamalı doğrulama henüz etkin değil." } }; },
      async unenroll() { return { error: { message: "İki aşamalı doğrulama henüz etkin değil." } }; },
    },
  },
  rpc: async (name: string, params?: unknown) => {
    try { const data = await apiRequest<unknown>(`/actions/${encodeURIComponent(name)}`, { method: "POST", body: params ?? {} }); return { data, error: null }; }
    catch (error) { return { data: null, error: { message: error instanceof Error ? error.message : "İşlem tamamlanamadı." } }; }
  },
  functions: {
    async invoke(name: string, input?: { body?: unknown }) {
      try { const data = await apiRequest<unknown>(`/actions/${encodeURIComponent(name)}`, { method: "POST", body: input?.body ?? {} }); return { data, error: null }; }
      catch (error) { return { data: null, error: { message: error instanceof Error ? error.message : "Servis çağrısı tamamlanamadı." } }; }
    },
  },
  storage: {
    from() {
      return {
        async upload() { return { data: null, error: { message: "Dosya depolama modülü henüz yapılandırılmadı." } }; },
        getPublicUrl() { return { data: { publicUrl: "" } }; },
      };
    },
  },
});
